import {
  FileSpreadsheet,
  Link2,
  Loader2,
  Lock,
  MoreHorizontal,
  RotateCcw,
  Trash2,
  Video,
} from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { StatusBadge } from '@/components/documents/DocumentBadges'
import { ROW_CHECKBOX_HIT_AREA } from '@/components/documents/document-grid'
import { ProcessingStages } from '@/components/processing/ProcessingStages'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { GridTableRow } from '@/components/ui/grid-table'
import { audioStageOf, isDocumentInFlight } from '@/features/documents/audio-stage'
import { formatDate, formatFileSize, getFileIconMeta } from '@/features/documents/format'
import { formatReceivedDay } from '@/features/documents/received-date'
import type { FolderTreeFile } from '@/features/documents/types'
import type { DocumentMapping, MappingState } from '@/services/api/structuredIngest'
import { cn } from '@/utils/cn'
import { formatExactDateTime } from '@/utils/date'
import { isTableFileType } from '@/utils/table-files'
import { isVideoFileType, isVideoUnavailable } from '@/utils/video-file'

export interface DocumentRowProps {
  file: FolderTreeFile
  isSelected: boolean
  index: number
  onToggle: (index: number) => void
  onDocumentSelect?: (documentId: string) => void
  isDetailSelected?: boolean
  onResetExtraction?: (documentId: string) => void
  /** Queues a recording's analysis on the video lane (PRD-0074); offered on video files only. */
  onAnalyseVideo?: (documentId: string) => void
  onDelete?: (documentId: string) => void
  /** Structured-ingest state of a tabular file (undefined for every other file). */
  mapping?: DocumentMapping
  onReviewMapping?: (analysisId: string) => void
  /** Starts preparing the mapping of a table that has none; left out while it cannot be offered. */
  onPrepareMapping?: (documentId: string) => void
  /** That start is on its way for this row. */
  startingMapping?: boolean
}

/** The small pills after a file's name; the ones that act on a click add `cursor-pointer`. */
const NAME_CHIP = 'inline-flex h-4 items-center gap-0.5 rounded px-1 text-3xs font-medium shrink-0'

const MAPPING_CHIP: Record<MappingState, { label: string; className: string }> = {
  preparing: { label: 'mapping…', className: 'bg-muted text-muted-foreground' },
  review: { label: 'review mapping', className: 'bg-primary/10 text-primary' },
  projected: { label: 'projected', className: 'bg-success/10 text-success' },
  failed: { label: 'mapping failed', className: 'bg-destructive/10 text-destructive' },
}

interface DocumentRowPhoneMetaProps {
  status: string
  fileType: string | null
  /** Already formatted. */
  size: string
  /** Already formatted; left out when unknown. */
  date: string | null
  /** State chips shown next to the status badge; with them the type / size / date
   *  text moves to a line of its own instead of being truncated to nothing. */
  children?: React.ReactNode
}

/**
 * Phone-only second line of a file row: the status, type, size and date that the
 * desktop table shows in their own columns.
 */
export function DocumentRowPhoneMeta({
  status,
  fileType,
  size,
  date,
  children,
}: DocumentRowPhoneMetaProps) {
  const meta = [(fileType || '-').toUpperCase(), size, date].filter(Boolean)
  return (
    <div
      className={cn(
        'mt-1 flex items-center gap-x-2 gap-y-1 text-2xs text-muted-foreground md:hidden',
        children && 'flex-wrap'
      )}
    >
      <StatusBadge status={status} />
      {children}
      <span className="truncate">{meta.join(' · ')}</span>
    </div>
  )
}

interface MappingChipProps {
  mapping: DocumentMapping
  onReview?: (analysisId: string) => void
  className?: string
}

/** Structured-ingest state of a tabular file; opens its mapping proposal. */
function MappingChip({ mapping, onReview, className }: MappingChipProps) {
  const chip = MAPPING_CHIP[mapping.state]
  if (!chip) return null
  return (
    <button
      type="button"
      title="Structured ingest — open the mapping proposal"
      className={cn(NAME_CHIP, 'cursor-pointer', chip.className, className)}
      onClick={e => {
        e.stopPropagation()
        onReview?.(mapping.analysisId)
      }}
    >
      {mapping.state === 'preparing' ? (
        <Loader2 className="size-2.5 animate-spin" />
      ) : (
        <FileSpreadsheet className="size-2.5" />
      )}
      {chip.label}
    </button>
  )
}

interface PrepareMappingChipProps {
  starting: boolean
  onPrepare: () => void
  className?: string
}

/** A table without a mapping: one click starts preparing it in the background, as the document detail does. */
function PrepareMappingChip({ starting, onPrepare, className }: PrepareMappingChipProps) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      disabled={starting}
      title={t('structuredIngest.prepare.hint')}
      className={cn(
        NAME_CHIP,
        'cursor-pointer bg-warning/10 text-warning disabled:cursor-default',
        className
      )}
      onClick={e => {
        e.stopPropagation()
        onPrepare()
      }}
    >
      {starting ? (
        <Loader2 className="size-2.5 animate-spin" />
      ) : (
        <FileSpreadsheet className="size-2.5" />
      )}
      {t('structuredIngest.prepare.chip')}
    </button>
  )
}

/** The day the case received the document (ADR-0094), which dates its events. */
function ReceivedCell({ time, locale }: { time: string | undefined; locale: string }) {
  return (
    <div className="text-xs whitespace-nowrap text-muted-foreground tabular-nums max-md:hidden">
      {formatReceivedDay(time, locale)}
    </div>
  )
}

/** A time column of the row: to the second, or '-' while there is none. */
function TimeCell({ time, locale }: { time: string | null | undefined; locale: string }) {
  return (
    <div className="text-xs whitespace-nowrap text-muted-foreground tabular-nums max-md:hidden">
      {time ? formatExactDateTime(time, locale) : '-'}
    </div>
  )
}

export const DocumentRow = React.memo(function DocumentRow({
  file,
  isSelected,
  index,
  onToggle,
  onDocumentSelect,
  isDetailSelected,
  onResetExtraction,
  onAnalyseVideo,
  onDelete,
  mapping,
  onReviewMapping,
  onPrepareMapping,
  startingMapping = false,
}: DocumentRowProps) {
  const { t, i18n } = useTranslation()
  const doc = file.document
  const { Icon, colorClass } = getFileIconMeta(doc.file_type)
  // A HUMINT capture is immutable evidence (ADR-0045): no reset, no delete, and the menu says why.
  const locked = doc.is_capture === true
  const lockReason = t('documents.capture.immutable')
  // An audio file on its way through transcription, or where it stopped; null for any other row.
  const stages = audioStageOf(doc)
  // A recording is analysed on the video lane (PRD-0074): offered once it is uploaded, again
  // after a run, and never while a job of any kind is on it.
  const video = isVideoFileType(doc.file_type) && onAnalyseVideo !== undefined
  const videoUnavailable = isVideoUnavailable(doc.file_type)
  // While a job of any kind is on the document the action waits (the API would answer 409).
  const videoBusy = isDocumentInFlight(doc.status)
  // The mapping state of a table, or the offer to start one when it has none yet.
  const mappingChip = (className: string) =>
    doc.tabular_extraction_mode === 'llm' ? (
      <span className={cn(NAME_CHIP, 'bg-muted text-muted-foreground', className)}>
        {t('structuredIngest.processing.llm.chip')}
      </span>
    ) : mapping ? (
      <MappingChip mapping={mapping} onReview={onReviewMapping} className={className} />
    ) : onPrepareMapping && isTableFileType(doc.file_type) ? (
      <PrepareMappingChip
        starting={startingMapping}
        onPrepare={() => onPrepareMapping(doc.id)}
        className={className}
      />
    ) : null
  return (
    <GridTableRow
      active={isDetailSelected}
      selected={isSelected}
      onClick={() => onDocumentSelect?.(doc.id)}
    >
      {/* The whole cell toggles: on the phone it is padded out to a row-high tap target. */}
      <div
        className={ROW_CHECKBOX_HIT_AREA}
        onClick={e => {
          e.stopPropagation()
          onToggle(index)
        }}
      >
        <Checkbox checked={isSelected} />
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-sm text-foreground truncate">
          <Icon className={cn('size-4 shrink-0', colorClass)} />
          <span className="truncate">{file.name}</span>
          {doc.child_of && (
            <span className={cn(NAME_CHIP, 'bg-cat-3/10 text-cat-3')}>
              <Link2 className="size-2.5" /> Lake
            </span>
          )}
          {locked && (
            <span title={lockReason} className={cn(NAME_CHIP, 'bg-muted text-muted-foreground')}>
              <Lock className="size-2.5" aria-hidden /> {t('documents.capture.badge')}
            </span>
          )}
          {/* The phone shows the chip on the line below, so the name keeps the row's width. */}
          {mappingChip('max-md:hidden')}
          {stages && (
            <ProcessingStages variant="chip" tone="pill" {...stages} className="max-md:hidden" />
          )}
        </div>
        <DocumentRowPhoneMeta
          status={doc.status}
          fileType={doc.file_type}
          size={formatFileSize(doc.file_size)}
          date={doc.created_at ? formatDate(doc.created_at) : null}
        >
          {/* As tall as the status badge; the pseudo-element pads the tap target to 36 px. */}
          {mappingChip('relative h-5 px-1.5 after:absolute after:inset-x-0 after:-inset-y-2') ??
            // A file is tabular or audio, never both: one chip at most, or the line as it was.
            (stages && (
              <ProcessingStages variant="chip" tone="pill" {...stages} className="h-5 px-1.5" />
            ))}
        </DocumentRowPhoneMeta>
      </div>
      <div className="text-xs text-muted-foreground uppercase max-md:hidden">
        {doc.file_type || '-'}
      </div>
      <div className="text-xs text-muted-foreground max-md:hidden">
        {formatFileSize(doc.file_size)}
      </div>
      <div className="text-xs max-md:hidden">
        <StatusBadge status={doc.status} />
      </div>
      <ReceivedCell time={doc.received_at} locale={i18n.language} />
      <TimeCell time={doc.created_at} locale={i18n.language} />
      <TimeCell time={doc.processed_at} locale={i18n.language} />
      <div
        className="opacity-0 group-hover:opacity-100 transition-opacity max-md:opacity-100"
        onClick={e => e.stopPropagation()}
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-8 md:size-6"
              onClick={e => e.stopPropagation()}
            >
              <MoreHorizontal className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          {/* A capture's menu is wide enough to read why its actions are disabled. */}
          <DropdownMenuContent align="end" className={cn(locked && 'w-64')}>
            {mapping && onReviewMapping && (
              <DropdownMenuItem onClick={() => onReviewMapping(mapping.analysisId)}>
                <FileSpreadsheet className="size-4 mr-2" /> Review mapping
              </DropdownMenuItem>
            )}
            {video && (
              <DropdownMenuItem
                disabled={locked || videoBusy || videoUnavailable}
                onClick={() => onAnalyseVideo?.(doc.id)}
              >
                <Video className="size-4 mr-2" />
                {t(
                  doc.status === 'uploaded'
                    ? 'documents.video.analyse'
                    : 'documents.video.analyseAgain'
                )}
              </DropdownMenuItem>
            )}
            {videoUnavailable && (
              <DropdownMenuLabel className="max-w-64 whitespace-normal text-xs font-normal text-muted-foreground">
                {t('documents.video.temporarilyUnavailable')}
              </DropdownMenuLabel>
            )}
            {!videoUnavailable && doc.status === 'processed' && onResetExtraction && (
              <DropdownMenuItem disabled={locked} onClick={() => onResetExtraction(doc.id)}>
                <RotateCcw className="size-4 mr-2" /> Reset Extraction
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              className="text-destructive"
              disabled={locked}
              onClick={() => onDelete?.(doc.id)}
            >
              <Trash2 className="size-4 mr-2" /> Delete
            </DropdownMenuItem>
            {locked && (
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                {lockReason}
              </DropdownMenuLabel>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </GridTableRow>
  )
})
